/** The five built-in arenas are BTC, ETH, SOL, TON and XAU; a sponsored arena uses its token's ticker. */
export type ArenaId = string;
export const BUILT_IN_ARENAS = ["BTC", "ETH", "SOL", "TON", "XAU"] as const;

export interface Arena {
  id: ArenaId;
  name: string;
  pair: string;
  /** Spot gold follows FX hours and closes over the weekend. */
  weekendClosed: boolean;
  /** A sponsor's arena: open only between these times, with its own stake ceiling. */
  sponsored?: { sponsor: string; maxStake: number; opensAt: number; closesAt: number };
}

/** Sponsored arenas are added to and taken off this list while the server runs (see sponsored.ts). */
export const ARENAS: Arena[] = [
  { id: "BTC", name: "Bitcoin", pair: "BTC/USD", weekendClosed: false },
  { id: "ETH", name: "Ethereum", pair: "ETH/USD", weekendClosed: false },
  { id: "SOL", name: "Solana", pair: "SOL/USD", weekendClosed: false },
  { id: "TON", name: "Toncoin", pair: "TON/USD", weekendClosed: false },
  { id: "XAU", name: "Gold", pair: "XAU/USD", weekendClosed: true },
];

/**
 * BTC is live from launch; the others open one by one in this order once BTC is busy enough
 * (build plan, "What unlocks what"). The admin flips each one open.
 */
export const LAUNCH_LOCKED: readonly ArenaId[] = ["ETH", "SOL", "TON", "XAU"];
/** Battles a day that fill the next arena's unlock bar. One of its tests; the admin makes the call. */
export const UNLOCK_BATTLES_PER_DAY = 2_000;

/** Battle lengths: 3:00 is the default, 1:00 is Blitz. Each has its own queue. 15 min and 1 hour come later. */
export const BATTLE_DURATIONS_SEC = [180, 60] as const;
export type BattleSec = (typeof BATTLE_DURATIONS_SEC)[number];
export const DEFAULT_BATTLE_SEC: BattleSec = 180;

/** A search nobody answers ends here on its own and the stake comes back, so a player is never stuck searching. */
export const SEARCH_LIMIT_SEC = 120;

export function getArena(id: string): Arena | undefined {
  return ARENAS.find((a) => a.id === id);
}

/**
 * Gold trades from Sunday 22:00 UTC to Friday 21:00 UTC, with a daily break 21:00 to 22:00 UTC.
 * These are the winter hours; the oracle's own market status flag is the final word in production.
 */
export function isArenaOpen(arena: Arena, now: Date, battleSec = 180): boolean {
  // A sponsored arena takes battles that finish before it closes.
  if (arena.sponsored) return now.getTime() >= arena.sponsored.opensAt && now.getTime() + battleSec * 1000 <= arena.sponsored.closesAt;
  if (!arena.weekendClosed) return true;
  const end = new Date(now.getTime() + battleSec * 1000);
  return isGoldOpen(now) && isGoldOpen(end);
}

function isGoldOpen(t: Date): boolean {
  const day = t.getUTCDay();
  const h = t.getUTCHours();
  if (day === 6) return false;
  if (day === 5 && h >= 21) return false;
  if (day === 0 && h < 22) return false;
  return h !== 21;
}
